import { computed, reactive } from "vue"
import { apiRequest } from "../services/apiClient"
import { type AuthUser, authService, type LoginData, type RegisterData } from "../services/authService"
import { logger } from "../utils/logger"
import { setSentryUser } from "../utils/sentry"

/**
 * The id this user's rows are stored under, which is NOT always the token's
 * subject. Identity Platform mints 28-character uids; every user_id column in
 * the schema is `uuid`, so the backend maps non-UUID subjects onto a stable
 * derived UUID. Accounts migrated from Supabase already had UUID subjects and
 * are returned unchanged.
 *
 * Everything in the app that asks "is this row mine?" compares against
 * currentUser.uid — member badges, "Deg" labels, settle-up, comment ownership,
 * and the household switcher's membership check. If that stayed the token
 * subject, all of them would silently answer "no" for every new account: an
 * invited member could accept an invitation and then be refused entry to the
 * household by the CLIENT, while the server considered them a member.
 *
 * Asked rather than recomputed, so the mapping has exactly one implementation.
 * On failure fall back to the subject — correct for migrated accounts, and no
 * worse than before for anyone else.
 */
async function resolveDatabaseUserId(subject: string): Promise<string> {
  try {
    const me = await apiRequest<{ userId?: string }>("/api/account/me")
    return me?.userId || subject
  } catch (error) {
    logger.error("Failed to resolve database user id; falling back to token subject", error)
    return subject
  }
}

interface AuthState {
  user: AuthUser | null
  isLoading: boolean
  isInitialized: boolean
  error: string | null
}

const state = reactive<AuthState>({
  user: null,
  isLoading: false,
  isInitialized: false,
  error: null,
})

// Computed properties
export const isAuthenticated = computed(() => !!state.user && !state.user.isAnonymous)
export const isAnonymous = computed(() => !!state.user && state.user.isAnonymous)
export const isGuest = computed(() => !state.user)
export const currentUser = computed(() => state.user)

// How long to wait for Firebase to report the restored session before giving
// up and rendering the signed-out screen. Restoring a cached session is a local
// read, so this only ever trips when something is actually wrong (blocked
// IndexedDB in the webview, a token refresh hanging on a captive portal).
// The app is unusable until this resolves, so it must always resolve.
const AUTH_INIT_TIMEOUT_MS = 8000

// Actions
export async function initializeAuth(): Promise<void> {
  if (state.isInitialized) return

  state.isLoading = true

  // Never rejects and never hangs: the whole UI is gated behind this promise,
  // so any failure has to settle it as "signed out" rather than leave the app
  // parked on the loading spinner forever. A late auth event is still picked up
  // by subscribeToAuthChanges.
  return new Promise((resolve) => {
    let settled = false
    let unsubscribe: (() => void) | null = null

    const settle = (user: AuthUser | null, error?: string) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      state.user = user
      state.isLoading = false
      state.isInitialized = true
      if (error) state.error = error
      // The listener can fire before onAuthStateChanged has returned its
      // unsubscribe function; unhooking is deferred to cover that ordering.
      queueMicrotask(() => unsubscribe?.())
      resolve()
    }

    const timer = setTimeout(() => {
      logger.warn("Auth initialization timed out; continuing as signed out")
      settle(null)
    }, AUTH_INIT_TIMEOUT_MS)

    try {
      unsubscribe = authService.onAuthStateChanged(
        (user) => settle(user),
        () => settle(null, "Authentication service unavailable"),
      )
    } catch (error) {
      logger.error("Failed to attach auth state listener", error)
      settle(null, "Authentication service unavailable")
    }
  })
}

// Resolves true when the user still has to confirm their email before they
// can sign in — the caller must show a "check your inbox" state instead of
// proceeding as authenticated.
export async function register(data: RegisterData): Promise<boolean> {
  state.isLoading = true
  state.error = null

  try {
    const { user, confirmationRequired } = await authService.register(data)
    if (!confirmationRequired) state.user = user
    return confirmationRequired
  } catch (error: any) {
    state.error = error.message
    throw error
  } finally {
    state.isLoading = false
  }
}

export async function login(data: LoginData): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    const user = await authService.login(data)
    state.user = user
  } catch (error: any) {
    state.error = error.message
    throw error
  } finally {
    state.isLoading = false
  }
}

export async function signInWithGoogle(): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    const user = await authService.signInWithGoogle()
    state.user = user
  } catch (error: any) {
    state.error = error.message
    throw error
  } finally {
    state.isLoading = false
  }
}

export async function signInWithApple(): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    const user = await authService.signInWithApple()
    state.user = user
  } catch (error: any) {
    state.error = error.message
    throw error
  } finally {
    state.isLoading = false
  }
}

export async function signInAnonymously(): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    const user = await authService.signInAnonymously()
    state.user = user
  } catch (error: any) {
    state.error = error.message
    throw error
  } finally {
    state.isLoading = false
  }
}

export async function linkAnonymousWithEmail(data: RegisterData): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    const user = await authService.linkAnonymousWithEmail(data)
    state.user = user
  } catch (error: any) {
    state.error = error.message
    throw error
  } finally {
    state.isLoading = false
  }
}

export async function linkAnonymousWithGoogle(): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    const user = await authService.linkAnonymousWithGoogle()
    state.user = user
  } catch (error: any) {
    state.error = error.message
    throw error
  } finally {
    state.isLoading = false
  }
}

export async function resetPassword(email: string): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    await authService.resetPassword(email)
  } catch (error: any) {
    state.error = error.message
    throw error
  } finally {
    state.isLoading = false
  }
}

export async function logout(): Promise<void> {
  state.isLoading = true
  state.error = null

  try {
    await authService.logout()
    state.user = null
    // Cached financial data must not outlive the session, and queued offline
    // mutations must never replay under the next user's token. Uploaded photos
    // are cached as object URLs, which outlive a sign-out unless revoked.
    const { clearApiReadCache, clearOutbox } = await import("../services/apiClient")
    clearApiReadCache()
    clearOutbox()
    const { clearImageCache } = await import("../services/imageService")
    clearImageCache()
  } catch (error: any) {
    state.error = error.message
    throw error
  } finally {
    state.isLoading = false
  }
}

export function clearError(): void {
  state.error = null
}

// Serialize auth transitions: onAuthStateChanged can fire rapidly (logout
// followed immediately by login), and the init/cleanup work is async. Without
// a queue, a logout's cleanup can race with the next user's init and leave
// dangling Firestore subscriptions or half-initialized stores.
let authTransitionChain: Promise<void> = Promise.resolve()
let lastHandledUid: string | undefined | null
let lastHandledWasAnonymous = false

export function subscribeToAuthChanges(): () => void {
  return authService.onAuthStateChanged((user) => {
    // Keep Sentry's user context in sync: set the id on login, clear on
    // logout. The id is the only thing sent (sentry.ts strips email/ip).
    setSentryUser(user?.uid ?? null)

    const previousUid = lastHandledUid
    const nextUid = user?.uid ?? null
    if (previousUid === nextUid) {
      state.user = user
      // Linking an anonymous account to email/Google keeps the SAME uid
      // (USER_UPDATED, not a sign-in), so the full-init branch below never
      // runs. Households are gated on "not anonymous + has email", meaning
      // an in-session guest upgrade would leave household features dead
      // until a full reload — initialize them here on that transition.
      const email = user?.email
      if (lastHandledWasAnonymous && user && !user.isAnonymous && email) {
        lastHandledWasAnonymous = false
        authTransitionChain = authTransitionChain.then(async () => {
          try {
            const { initializeHouseholds, restoreActiveContext } = await import("./householdStore")
            await initializeHouseholds(user.uid, email)
            await restoreActiveContext()
          } catch (error) {
            logger.error("Failed to initialize households after account upgrade", error)
          }
        })
      }
      return
    }
    lastHandledUid = nextUid
    lastHandledWasAnonymous = user?.isAnonymous ?? false

    authTransitionChain = authTransitionChain.then(async () => {
      state.user = user ? { ...user, uid: await resolveDatabaseUserId(user.uid) } : user

      // The uid changed, so any cached AI insights belong to the previous
      // user/context and must not leak into the new one.
      try {
        const { aiInsights } = await import("./aiInsights")
        aiInsights.clearCache()
      } catch (error) {
        logger.error("Failed to clear AI insights cache", error)
      }

      // Everything the previous signed-in user left behind: data stores,
      // household context, chat, read cache, queued offline mutations. Runs
      // on sign-out AND before initializing a different user — a direct
      // session swap (e.g. a signed-in/anonymous session replaced via
      // signInWithPassword/OAuth) emits only SIGNED_IN, never SIGNED_OUT,
      // and without this user B keeps seeing A's data on screen while A's
      // outbox replays under B's token.
      const teardownPreviousUser = async () => {
        try {
          const { cleanupFirestore } = await import("./firestoreStore")
          cleanupFirestore()
        } catch (error) {
          logger.error("Failed to cleanup Firestore", error)
        }

        try {
          const { resetSubscription } = await import("./subscriptionStore")
          resetSubscription()
        } catch (error) {
          logger.error("Failed to reset subscription", error)
        }

        try {
          const { cleanupHouseholds } = await import("./householdStore")
          cleanupHouseholds()
        } catch (error) {
          logger.error("Failed to cleanup households", error)
        }

        // Drop the assistant conversation so the next user on this device
        // can't read the previous user's financial chat.
        try {
          const { resetChat } = await import("./aiChatStore")
          resetChat()
        } catch (error) {
          logger.error("Failed to reset AI chat", error)
        }

        // The cache key isn't uid-scoped, so anything left here would hydrate
        // or replay under the next user's token.
        try {
          const { clearApiReadCache, clearOutbox } = await import("../services/apiClient")
          clearApiReadCache()
          clearOutbox()
          const { clearImageCache } = await import("../services/imageService")
          clearImageCache()
        } catch (error) {
          logger.error("Failed to clear API cache/outbox", error)
        }
      }

      if (user) {
        // previousUid is undefined on the very first auth event (nothing to
        // tear down) and null when the last event was a sign-out (teardown
        // already ran there).
        if (previousUid) {
          await teardownPreviousUser()
        }

        // Drain anything the outbox queued before this session had a token.
        // initOutbox() replays once at module init and then only on an 'online'
        // event — but a mutation can fail at the network level while the device
        // never actually goes offline (transient DNS, a Cloud Run cold start,
        // any fetch that throws). That queues an entry, and the init replay
        // races the token restore: it 401s, deliberately keeps the entry, and
        // no 'online' event ever follows to retry it. The entry then sits
        // forever behind a "syncing N changes…" banner that never clears.
        // Replaying here is the first moment a token is guaranteed. No-ops on
        // an empty queue, so it costs one localStorage read on a normal login.
        try {
          const { replayOutbox } = await import("../services/apiClient")
          await replayOutbox()
        } catch (error) {
          logger.error("Failed to replay offline outbox after sign-in", error)
        }

        try {
          const { initializeFirestore } = await import("./firestoreStore")
          await initializeFirestore(user)
        } catch (error) {
          logger.error("Failed to initialize Firestore", error)
        }

        try {
          const { initializeSubscription } = await import("./subscriptionStore")
          await initializeSubscription(user.uid)
        } catch (error) {
          logger.error("Failed to initialize subscription", error)
        }

        if (!user.isAnonymous && user.email) {
          try {
            const { initializeHouseholds, restoreActiveContext } = await import("./householdStore")
            await initializeHouseholds(user.uid, user.email)
            await restoreActiveContext()
          } catch (error) {
            logger.error("Failed to initialize households", error)
          }
        }
      } else {
        // Sign-outs that don't go through the logout() action (refresh-token
        // expiry, revocation) land here too and must drop the same state.
        await teardownPreviousUser()
      }
    })
  })
}

// Export the reactive state for direct access if needed
export const authStore = {
  state,
  isAuthenticated,
  isAnonymous,
  isGuest,
  currentUser,
}
